/*
Purpose: Shared list shell for the four Calendar (v2) request inboxes.
*/

import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { capitalize } from '../session-model';

/** `YYYY-MM-DD` plus `HH:mm:ss` rendered as "Mon 04 Aug 2025 · 16:00". */
function formatWhen(date, time) {
  const parsed = Moment(date, 'YYYY-MM-DD');
  const datePart = parsed.isValid() ? parsed.format('ddd DD MMM YYYY') : date || '—';
  return time ? `${datePart} · ${String(time).slice(0, 5)}` : datePart;
}

function studentNameOf(record) {
  return `${capitalize(record.st_first_name)} ${record.st_surname || ''}`.trim();
}

function tutorNameOf(record) {
  return `${capitalize(record.tut_fname)} ${record.tut_surname || ''}`.trim();
}

/** One request row. */
function InboxCard({ record, isSelectable, isSelected, onToggleSelect, renderActions }) {
  const slotCount = parseInt(record.slot_count, 10);

  return (
    <article className="cv2-inbox-card">
      {isSelectable ? (
        <div className="cv2-inbox-card__check">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(record)}
            aria-label={`Select ${studentNameOf(record)}`}
          />
        </div>
      ) : null}

      <div className="cv2-inbox-card__body">
        <div className="cv2-inbox-card__title-row">
          <span className="cv2-inbox-card__name">{studentNameOf(record)}</span>
          {record.subject_name ? (
            <span className="cv2-inbox-card__subject">{record.subject_name}</span>
          ) : null}
          {Number.isFinite(slotCount) && slotCount > 0 ? (
            <span className="cv2-inbox-card__slots">
              {slotCount} slot{slotCount === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>

        <div className="cv2-inbox-card__meta">{formatWhen(record.sDate, record.sTime)}</div>
        <div className="cv2-inbox-card__tutor">{tutorNameOf(record) || '—'}</div>

        {renderActions ? (
          <div className="cv2-inbox-card__actions">{renderActions(record)}</div>
        ) : null}
      </div>
    </article>
  );
}

/**
 * @param {Object} props
 * @param {string} props.title Heading shown above the list.
 * @param {string} props.subtitle One-line explanation of what the inbox contains.
 * @param {Object[]} props.records Rows straight from the backend.
 * @param {Function} [props.renderActions] Returns the action buttons for one row.
 * @param {Function} [props.getRecordKey] Row identity; defaults to `cancel_session_key`.
 * @param {Function} [props.isRecordSelectable] Decides per row whether a checkbox shows.
 * @param {React.ReactNode} [props.footer] Rendered under the list, e.g. a bulk action.
 */
export default function InboxView({
  title,
  subtitle,
  icon: Icon,
  records,
  emptyMessage,
  renderActions,
  getRecordKey,
  isRecordSelectable,
  selectedKeys,
  onToggleSelect,
  footer,
  onBack,
  embedded,
}) {
  return (
    <section className={embedded ? 'cv2-inbox cv2-inbox--embedded' : 'cv2-inbox'}>
      {embedded ? (
        <p className="cv2-inbox__subtitle">{subtitle}</p>
      ) : (
        <header className="cv2-inbox__head">
          <button type="button" className="cv2-btn cv2-inbox__back" onClick={onBack}>
            <ArrowBackIcon className="cv2-icon" />
            Back to calendar
          </button>
          <h1 className="cv2-inbox__title">
            {Icon ? <Icon className="cv2-icon" style={{ fontSize: 20 }} /> : null}
            {title}
            <span className="cv2-title__meta">{records.length}</span>
          </h1>
          <span className="cv2-inbox__subtitle">{subtitle}</span>
        </header>
      )}

      {records.length === 0 ? (
        <div className="cv2-empty cv2-empty--inbox">
          <span className="cv2-empty__title">Nothing to action</span>
          {emptyMessage}
        </div>
      ) : (
        <div className="cv2-inbox__list">
          {records.map((record) => {
            const key = getRecordKey(record);
            return (
              <InboxCard
                key={key}
                record={record}
                isSelectable={isRecordSelectable(record)}
                isSelected={selectedKeys.includes(key)}
                onToggleSelect={onToggleSelect}
                renderActions={renderActions}
              />
            );
          })}
        </div>
      )}

      {footer && records.length > 0 ? <div className="cv2-inbox__foot">{footer}</div> : null}
    </section>
  );
}

const recordShape = PropTypes.shape({
  st_first_name: PropTypes.string,
  st_surname: PropTypes.string,
  subject_name: PropTypes.string,
  sDate: PropTypes.string,
  sTime: PropTypes.string,
  tut_fname: PropTypes.string,
  tut_surname: PropTypes.string,
});

InboxView.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  records: PropTypes.arrayOf(recordShape).isRequired,
  emptyMessage: PropTypes.string,
  renderActions: PropTypes.func,
  getRecordKey: PropTypes.func,
  isRecordSelectable: PropTypes.func,
  selectedKeys: PropTypes.array,
  onToggleSelect: PropTypes.func,
  footer: PropTypes.node,
  onBack: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
};

InboxView.defaultProps = {
  icon: null,
  emptyMessage: 'There are no items in this list right now.',
  renderActions: undefined,
  getRecordKey: (record) => record.cancel_session_key,
  isRecordSelectable: () => false,
  selectedKeys: [],
  onToggleSelect: () => { },
  footer: null,
  embedded: false,
};

InboxCard.propTypes = {
  record: recordShape.isRequired,
  isSelectable: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  renderActions: PropTypes.func,
};

InboxCard.defaultProps = {
  renderActions: undefined,
};
